import React, { useEffect, useRef, useState } from 'react';
import { connect } from "react-redux";
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faLock } from '@fortawesome/free-solid-svg-icons'
import { formatCreditCard, formatDate } from 'cleave-zen';
import { Modal } from 'react-bootstrap';
import qs from 'qs';
import moment from 'moment';
import { faQuestionCircle, faSquare, faCheckSquare } from '@fortawesome/free-regular-svg-icons'
import { getLink } from "../../dataProvider/links.js"
import { validate as validateVATNumber } from '../../dataProvider/vat.js';
import { isValidEmail } from '../../dataProvider/user.js';
import { makePayment, reloadUser, storeEmail, updateUser } from '../../actions/user.js'
import { SET_PRODUCT } from '../../actions/product.js';
import { error as logError } from "../../logger.js";

import secureMethod from "../../img/v1/payment/secureMethod.png"
import cvcHelp from "../../img/v1/payment/cvcHelp.jpg"
import payButtonIcon from "../../img/v1/payment/payButtonIcon.svg"
import { DisplayTimeout } from './Payment.jsx';

import "../../scss/v2/payment.scss"

function PaymentV1(props) {

    const { t, i18n } = useTranslation();
    let navigate = useNavigate();
    let location = useLocation();
    const inputExpirationDateRef = useRef(null);
    const inputCvcRef = useRef(null);
    const inputNameRef = useRef(null);
    const [customerType, setCustomerType] = useState('normal')
    const [reason, setReason] = useState('')
    const [vatNumber, setVatNumber] = useState('');
    const [address, setAddress] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [expirationDateError, setExpirationDateError] = useState(false);
    const [cvc, setCvc] = useState('');
    const [name, setName] = useState(null);
    const [email, setEmail] = useState(props.email || '');
    const [agreed, setAgreed] = useState(false);
    const [error, setError] = useState(qs.parse(location.search, { ignoreQueryPrefix: true }).error);
    const [hoverCvc, setHoverCvc] = useState(false);
    const [pending, setPending] = useState(false);
    const [seeMore, setSeeMore] = useState(false);
    const [modal, setModal] = useState(false);
    const [modalMode, setModalMode] = useState("gsc");
    const [displayTimer, setDisplayTimer] = useState(false);
    const [timeout, setTimeout] = useState(999);

    useEffect(() => {
        setDisplayTimer(props?.displayTimer);
        setTimeout(props?.timeout);
    }, [props?.displayTimer, props?.timeout])

    useEffect(() => {
        const product = props.products.find(product => product.type === customerType)
        if (product) props.dispatch({ type: SET_PRODUCT, product });
        if (props.onCustomerTypeChange) props.onCustomerTypeChange(customerType)
    }, [customerType])

    useEffect(() => {
        async function saveEmail() {
            try {
                if (isValidEmail(email)) {
                    await props.dispatch(storeEmail(email));
                }
            } catch (error) {
                logError(error)
            }
        }
        const timeout = window.setTimeout(saveEmail, 3000)

        return () => {
            window.clearTimeout(timeout)
        }
    }, [email])

    async function handleEmail() {
        try {
            if (!isValidEmail(email)) {
                setEmail('');
                setError('email');
            }
            await props.dispatch(storeEmail(email));
        } catch (error) {
            logError(error)
        }
    }

    const handleClose = () => {
        setAgreed(true)
        setModal(false)
    }

    const handleAgreementClick = () => {
        setAgreed(!agreed);
        if (error === "agreement") { setError(null); }
    }

    async function handleSubmit(e) {
        try {
            e.preventDefault();
            setError(null);

            await handleEmail();

            if (!agreed) return setError("agreement");

            if (expirationDateError) return;

            setPending(true);

            let user = await props.dispatch(reloadUser())

            if (!user.metadata) user.metadata = {}
            user.metadata.customerType = customerType;

            if (customerType === 'company') {
                const vatResult = await validateVATNumber(vatNumber)
                if (!vatResult.isValid) {
                    setPending(false);
                    return setError("vat");
                }
                user.metadata.reason = reason;
                user.metadata.vatNumber = vatNumber;
                user.metadata.vatRate = 0;
                user.metadata.address = address;
            }

            await props.dispatch(updateUser({ id: user.id, metadata: user.metadata, email: props.email }))

            const charge = await props.dispatch(makePayment({ card: cardNumber, date: expirationDate, cvc, name }))

            if (charge.nextAction) return navigate(getLink('paymentSecure', i18n.language))

            user = await props.dispatch(reloadUser())

            if (user.status !== 'ok') return handlePaymentError();

            setPending(false);
            navigate(getLink('paymentConfirmation', i18n.language))
        } catch (err) {
            logError(err);
            if (error != 'email') handlePaymentError()
        }
    }

    function handlePaymentError() {
        setError("server");
        setPending(false);
        setCvc('');
        setExpirationDate('')
        setCardNumber('')
    }

    function renderError() {
        if (!error) return;
        return (
            <div className="col-12" >
                <div className="alert alert-danger my-3" >{t(`payment.formCard.error.${error}`)}</div>
            </div>
        );
    }

    function renderExpirationDateError() {
        if (!expirationDateError) return;
        return (
            <div className='alert alert-danger' >{t('payment.formCard.label.expirationError')}</div>
        );
    }

    function renderPaymentButton() {
        let content = (
            <>
                <img className="img img-fluid payment-blue-button-icon" src={payButtonIcon} alt={payButtonIcon} />
                <span>{t('payment.formCard.button.text')}</span>
            </>
        );

        if (pending) content = (
            <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
            </div>
        );

        return (
            <div className="text-center">
                <button className="button orange-button payment-blue-button" type="submit" disabled={pending} >{content}</button>
            </div>
        );
    }

    const handleCardNumberChange = (e) => {
        const formatted = formatCreditCard(e.target.value);
        setCardNumber(formatted);
        if (formatted.length >= 19) {
            inputExpirationDateRef.current.focus();
        }
    };

    const handleExpirationDateChange = (e) => {
        const formatted = formatDate(e.target.value, {
            datePattern: ['m', 'y'],
        });
        setExpirationDate(formatted);
        if (expirationDateError) setExpirationDateError(false);
        if (formatted.length >= 5) {
            const year = formatted.split('/')[1];
            if (parseInt(year) < parseInt(moment().format('YY'))) setExpirationDateError(true);
            inputCvcRef.current.focus();
        }
    };

    return (
        <div className="payment-formCard-container v1">
            <div className="payment-formCard-header">
                <div className="payment-formCard-header-secure">
                    <FontAwesomeIcon icon={faLock} />
                    <p className="payment-formCard-header-secure-text">{t('payment.formCard.header.secure.text')}</p>
                </div>
                <div className="row payment-formCard-header-mobile">
                    <div className="col-12 col-lg-10 offset-lg-1">
                        <div className="row">
                            <div className="col-6">
                                <p className="payment-formCard-header-price-text">{t(`payment.formCard.header.price.${customerType}.text`)}</p>
                            </div>
                            <div className="col-6">
                                <div className="payment-formCard-header-price-value-container payment-formCard-header-left-column">
                                    <p className="payment-formCard-header-price-value">{t('payment.formCard.header.price.value', { acqPrice: props.product.acqPrice, subPrice: props.product.subPrice })}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <form className="payment-formCard-body" onSubmit={handleSubmit}>
                <div className="row">
                    {renderError()}
                    <div className="col-12 col-lg-10 offset-lg-1">
                        <div className="row form-inputs-container">
                            {(displayTimer) ? (
                                <div className='col-12'>
                                    <DisplayTimeout timeout={timeout} />
                                </div>
                            ) : null}
                            <div className='col-12' >
                                <div className='payment-formCard-input-group'>
                                    <div className='form-check form-check-inline' >
                                        <input
                                            className='form-check-input'
                                            type="radio"
                                            name="customerType"
                                            value="normal"
                                            id="customerTypeNormal"
                                            checked={customerType === 'normal'}
                                            onChange={e => setCustomerType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="customerTypeNormal">{t('payment.formCard.label.customerTypeNormal')}</label>
                                    </div>
                                    <div className='form-check form-check-inline' >
                                        <input
                                            className='form-check-input'
                                            type="radio"
                                            name="customerType"
                                            value="company"
                                            id="customerTypeCompany"
                                            checked={customerType === 'company'}
                                            onChange={e => setCustomerType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="customerTypeCompany">{t('payment.formCard.label.customerTypeCompany')}</label>
                                    </div>
                                    <div className='form-check form-check-inline' >
                                        <input
                                            className='form-check-input'
                                            type="radio"
                                            name="customerType"
                                            value="selfEmployed"
                                            id="customerTypeSelfEmployed"
                                            checked={customerType === 'selfEmployed'}
                                            onChange={e => setCustomerType(e.target.value)}
                                        />
                                        <label className="form-check-label" htmlFor="customerTypeSelfEmployed">{t('payment.formCard.label.customerTypeSelfEmployed')}</label>
                                    </div>
                                </div>
                            </div>
                            {customerType === 'company' && (
                                <>
                                    <div className="col-6">
                                        <div className="payment-formCard-input-group">
                                            <p className="payment-formCard-input-group-label">{t('payment.formCard.label.reason')}</p>
                                            <input
                                                className="payment-formCard-input-group-input"
                                                placeholder={t('payment.formCard.placeholder.reason')}
                                                value={reason}
                                                onChange={(e) => { setReason(e.target.value) }}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="payment-formCard-input-group">
                                            <p className="payment-formCard-input-group-label">{t('payment.formCard.label.vatNumber')}</p>
                                            <input
                                                className="payment-formCard-input-group-input"
                                                placeholder={t('payment.formCard.placeholder.vatNumber')}
                                                value={vatNumber}
                                                onChange={(e) => { setVatNumber(e.target.value) }}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-12">
                                        <div className="payment-formCard-input-group">
                                            <p className="payment-formCard-input-group-label">{t('payment.formCard.label.address')}</p>
                                            <input
                                                className="payment-formCard-input-group-input"
                                                placeholder={t('payment.formCard.placeholder.address')}
                                                value={address}
                                                onChange={(e) => { setAddress(e.target.value) }}
                                                required
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            <div className="col-12">
                                <div className="payment-formCard-input-group">
                                    <p className="payment-formCard-input-group-label">{t('payment.formCard.label.cardNumber')}</p>
                                    <img className="img img-fluid payment-formCard-input-group-label-image" src={secureMethod} alt={secureMethod} />
                                    <input
                                        placeholder={t('payment.formCard.placeholder.cardNumber')}
                                        className="payment-formCard-input-group-input"
                                        options={{ creditCard: true }}
                                        required
                                        inputMode="numeric"
                                        pattern="(\d{4} ){3}\d{4}"
                                        autoComplete="cc-number"
                                        value={cardNumber}
                                        onChange={handleCardNumberChange} />
                                    <div className="payment-formCard-input-group-icon"><FontAwesomeIcon icon={faLock} /></div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="payment-formCard-input-group">
                                    <p className="payment-formCard-input-group-label">{t('payment.formCard.label.expiration')}</p>
                                    <input placeholder={t('payment.formCard.placeholder.expiration')}
                                        ref={inputExpirationDateRef}
                                        className={`payment-formCard-input-group-input ${expirationDateError ? 'error' : ''}`}
                                        required
                                        pattern="\d{2}\/\d{2}"
                                        inputMode="numeric"
                                        autoComplete="cc-exp"
                                        value={expirationDate}
                                        onChange={handleExpirationDateChange} />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="payment-formCard-input-group">
                                    <p className="payment-formCard-input-group-label">{t('payment.formCard.label.cvc')}</p>
                                    <input
                                        ref={inputCvcRef}
                                        className="payment-formCard-input-group-input"
                                        placeholder={t('payment.formCard.placeholder.cvc')}
                                        inputMode="numeric" maxLength="3"
                                        pattern="\d*"
                                        autoComplete="cc-csc"
                                        value={cvc}
                                        onChange={(event) => {
                                            if (isNaN(event.target.value[event.target.value.length - 1])) {
                                                //if not a digit remove char
                                                event.target.value = event.target.value.slice(0, -1)
                                            }

                                            if (event.target.value.length >= 3) {
                                                inputNameRef.current.focus();
                                            }
                                            setCvc(event.target.value)

                                            if (event.target.value.length >= 3) {
                                                inputNameRef.current.focus();
                                            }
                                        }}
                                        required
                                    />
                                    <div className="payment-formCard-tooltip-cvc" onMouseEnter={() => { setHoverCvc(true) }} onMouseLeave={() => { setHoverCvc(false) }}>
                                        <div className="payment-formCard-input-group-icon">
                                            <FontAwesomeIcon icon={faQuestionCircle} />
                                        </div>
                                        {hoverCvc ? <img className="img img-fluid payment-formCard-tooltip-cvc-image" alt={cvcHelp} src={cvcHelp} /> : ""}
                                    </div>
                                </div>
                            </div>
                            <div className="col-12">{renderExpirationDateError()}</div>
                            <div className="col-6">
                                <div className="payment-formCard-input-group">
                                    <p className="payment-formCard-input-group-label">{t('payment.formCard.label.name')}</p>
                                    <input
                                        ref={inputNameRef}
                                        className="payment-formCard-input-group-input"
                                        placeholder={t('payment.formCard.placeholder.name')}
                                        autoComplete="cc-name"
                                        onChange={(event) => { setName(event.target.value) }}
                                        required
                                    />
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="payment-formCard-input-group">
                                    <p className="payment-formCard-input-group-label">{t('payment.formCard.label.email')}</p>
                                    <input className="payment-formCard-input-group-input" placeholder={t('payment.formCard.placeholder.email')} onChange={(event) => { setEmail(event.target.value) }} required type='email' value={email} />
                                </div>
                            </div>
                            <div className="col-12">
                                <div className={`payment-formCard-checkbox-group${error === "agreement" ? ' error' : ''}`}>
                                    <div className="d-inline-block mr-2" onClick={handleAgreementClick}>
                                        {agreed ? <FontAwesomeIcon icon={faCheckSquare} /> : <FontAwesomeIcon icon={faSquare} />}
                                    </div>
                                    <div className="payment-formCard-checkbox-group-label-container">
                                        <span className="payment-formCard-checkbox-group-label mr-1" onClick={handleAgreementClick}>{t('payment.formCard.label.checkbox.1')}</span>
                                        <span className="payment-formCard-checkbox-group-label blue-text" onClick={() => { setModal(true); setModalMode("gsc"); }}>{t('payment.formCard.label.checkbox.2')}</span>
                                        <span className="payment-formCard-checkbox-group-label mr-1" onClick={handleAgreementClick}>{t('payment.formCard.label.checkbox.3')}</span>
                                        <span className="payment-formCard-checkbox-group-label blue-text" onClick={() => { setModal(true); setModalMode("subscription"); }}>{t('payment.formCard.label.checkbox.5')}</span>
                                        <span className="payment-formCard-checkbox-group-label mr-1" dangerouslySetInnerHTML={{ __html: t(`payment.formCard.label.checkbox.6.${customerType}`, { acqPrice: props.product.acqPrice, subPrice: props.product.subPrice }) }} onClick={handleAgreementClick} />
                                        <span className="payment-formCard-checkbox-group-label blue-text" onClick={() => { setSeeMore(!seeMore) }}>{t('payment.formCard.label.checkbox.4')}</span>
                                    </div>
                                </div>
                                {seeMore && <p className="payment-formCard-seeMore-text">{t('payment.formCard.seeMore.text', { acqPrice: props.product.acqPrice, subPrice: props.product.subPrice })}</p>}
                                {renderPaymentButton()}
                            </div>
                        </div>
                    </div>
                </div>
            </form>
            <Modal id="payment-modal" show={modal} onHide={() => { setModal(false) }}>
                <Modal.Header closeButton>
                    {
                        modalMode === "subscription" ?
                            <p>{t('payment.subscription.title')}</p> :
                            <p>{t('payment.gsc.title')}</p>
                    }
                </Modal.Header>
                <Modal.Body>
                    {
                        modalMode === "subscription" ?
                            <div className="payment-legal-text" dangerouslySetInnerHTML={{ __html: t('legal.subscription.text', { acqPrice: props.product.acqPrice, subPrice: props.product.subPrice, currency: props.product.currency, currencyCode: props.product.currencyCode }) }} /> :
                            <div className="payment-legal-text" dangerouslySetInnerHTML={{ __html: t('legal.gsc.text', { acqPrice: props.product.acqPrice, subPrice: props.product.subPrice, currency: props.product.currency, currencyCode: props.product.currencyCode }) }} />
                    }
                </Modal.Body>
                <Modal.Footer>
                    <button className="button orange-button" onClick={handleClose}>{t('payment.gsc.button')}</button>
                </Modal.Footer>
            </Modal>
        </div>
    )

}

const mapStateToProps = state => ({
    mode: state.mode.mode,
    products: state.product.products,
    product: state.product.product,
    email: state.user.email
})

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(mapStateToProps, mapDispatchToProps)(PaymentV1);